import { useEffect } from 'react';
import Hero from './components/Hero';
import PainPoint from './components/PainPoint';
import Services from './components/Services';
import USPComparison from './components/USPComparison';
import Process from './components/Process';
import SelfDiagnosis from './components/SelfDiagnosis';
import Testimonials from './components/Testimonials';
import FAQ from './components/FAQ';
import FinalCTA from './components/FinalCTA';
import Footer from './components/Footer';
import Header from './components/Header';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://www.mustsafe.co.kr';

export default function HomePage() {
  useEffect(() => {
    const schemas = [
      {
        id: 'schema-organization',
        data: {
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "중대안전보건예방협회",
          "alternateName": "MUSTSAFE",
          "url": SITE_URL,
          "logo": `${SITE_URL}/logo.png`,
          "description": "위험성평가, 근골격계유해요인조사, 안전보건체계구축, 유해위험방지계획서, 안전관리 위탁(대행), KCs인증 전문 컨설팅 기관",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "가산디지털1로 168 B동 910호",
            "addressLocality": "금천구",
            "addressRegion": "서울특별시",
            "postalCode": "08507",
            "addressCountry": "KR"
          },
          "contactPoint": {
            "@type": "ContactPoint",
            "email": "mustsafe2@gmail.com",
            "contactType": "customer service",
            "availableLanguage": "Korean"
          },
          "faxNumber": "02-857-3309",
          "sameAs": ["https://www.mustsafe.co.kr/"]
        }
      },
      {
        id: 'schema-local-business',
        data: {
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "name": "중대안전보건예방협회",
          "image": `${SITE_URL}/logo.png`,
          "url": SITE_URL,
          "description": "산업안전보건 전문 컨설팅. 위험성평가, 근골격계유해요인조사, 안전보건체계구축, 유해위험방지계획서, 안전관리 위탁, KCs인증 서비스 제공.",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "가산디지털1로 168 B동 910호",
            "addressLocality": "금천구",
            "addressRegion": "서울특별시",
            "postalCode": "08507",
            "addressCountry": "KR"
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": 37.4784,
            "longitude": 126.8826
          },
          "openingHoursSpecification": [
            {
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday"],
              "opens": "09:00",
              "closes": "18:00"
            }
          ],
          "priceRange": "$$"
        }
      },
      {
        id: 'schema-webpage',
        data: {
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "중대안전보건예방협회 | 위험성평가·근골격계조사·안전보건 컨설팅 전문",
          "description": "위험성평가, 근골격계유해요인조사, 안전보건체계구축, 유해위험방지계획서, 안전관리 위탁(대행), KCs인증 전문 컨설팅. 사업장 안전사고 예방과 과태료 방지.",
          "url": SITE_URL,
          "inLanguage": "ko-KR",
          "isPartOf": {
            "@type": "WebSite",
            "name": "중대안전보건예방협회",
            "url": SITE_URL
          }
        }
      },
      {
        id: 'schema-faq',
        data: {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "컨설팅 비용은 얼마인가요?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "컨설팅 비용은 업종, 사업장 규모, 근로자 수, 서비스 종류에 따라 달라집니다. 무료 견적 신청을 통해 정확한 비용을 안내받으실 수 있으며, 영업일 기준 3일 이내에 견적서를 발송해드립니다."
              }
            },
            {
              "@type": "Question",
              "name": "컨설팅 기간은 얼마나 걸리나요?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "서비스 종류에 따라 다르지만, 일반적으로 위험성평가는 2~3주, 근골격계유해요인조사는 3~4주, 안전보건체계구축은 4~6주 정도 소요됩니다. 급한 경우 별도 협의를 통해 일정 조율이 가능합니다."
              }
            },
            {
              "@type": "Question",
              "name": "어떤 업종도 가능한가요?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "네, 모든 업종의 컨설팅이 가능합니다. 제조업, 건설업, 서비스업, 물류업 등 다양한 업종에서 풍부한 경험을 보유하고 있으며, 각 업종의 특성에 맞는 맞춤형 컨설팅을 제공합니다."
              }
            },
            {
              "@type": "Question",
              "name": "고용노동부 인증 기관이라는 것은 무엇을 의미하나요?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "고용노동부로부터 정식으로 인증받은 교육기관으로, 산업안전보건 관련 직무교육을 진행할 수 있는 자격을 갖춘 기관입니다. 이는 전문성과 신뢰성을 공식적으로 인정받았다는 의미입니다."
              }
            },
            {
              "@type": "Question",
              "name": "사후관리는 어떻게 진행되나요?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "컨설팅 완료 후에도 감독 대응을 위한 증빙자료를 지속적으로 관리해드리며, 추가 문의사항이나 개선사항에 대해 상담을 제공합니다. 정기적인 모니터링을 통해 지속적인 안전관리를 지원합니다."
              }
            },
            {
              "@type": "Question",
              "name": "무료 자료는 어떻게 받을 수 있나요?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "무료 자료 신청 시, 컨설팅을 신청하지 않아도 2026년 최신 감독 지적 사례집과 적발 사례 Top 4 자료를 무료로 제공해드립니다. 또한 자가진단 체크리스트와 세부 분석 리포트, 업종별 유의사항도 함께 받으실 수 있습니다."
              }
            },
            {
              "@type": "Question",
              "name": "현장 방문은 필수인가요?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "대부분의 컨설팅은 정확한 현황 파악을 위해 현장 방문이 필요합니다. 다만, 서비스 종류에 따라 일부는 원격으로 진행 가능한 경우도 있으니 상담 시 문의해주시기 바랍니다."
              }
            },
            {
              "@type": "Question",
              "name": "계약 후 취소나 환불이 가능한가요?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "계약 후 작업 진행 전에는 전액 환불이 가능하며, 작업 진행 정도에 따라 부분 환불이 가능합니다. 자세한 환불 규정은 계약서에 명시되어 있으며, 상담 시 안내해드립니다."
              }
            }
          ]
        }
      },
      {
        id: 'schema-service',
        data: {
          "@context": "https://schema.org",
          "@type": "Service",
          "serviceType": "안전보건 컨설팅",
          "provider": {
            "@type": "Organization",
            "name": "중대안전보건예방협회"
          },
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "안전보건 컨설팅 서비스",
            "itemListElement": [
              { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "위험성평가", "description": "사업장 내 유해·위험요인을 파악하고 개선대책을 수립하여 산업재해를 예방합니다." } },
              { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "근골격계유해요인조사", "description": "근골격계 질환 예방을 위한 작업환경 조사 및 개선방안을 제시합니다." } },
              { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "안전보건체계구축", "description": "체계적인 안전보건관리 시스템을 구축하여 사업장의 안전문화를 정착시킵니다." } },
              { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "유해위험방지계획서", "description": "건설공사 착공 전 필수 제출 서류로, 안전한 공사 진행을 위한 계획을 수립합니다." } },
              { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "안전관리 위탁(대행)", "description": "전문 안전관리자가 귀사의 안전관리 업무를 전담하여 수행합니다." } },
              { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "KCs인증", "description": "국가통합인증마크 취득을 위한 전 과정을 지원합니다." } }
            ]
          }
        }
      }
    ];

    schemas.forEach(({ id, data }) => {
      const existing = document.getElementById(id);
      if (existing) existing.remove();
      const script = document.createElement('script');
      script.id = id;
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(data);
      document.head.appendChild(script);
    });

    return () => {
      schemas.forEach(({ id }) => {
        const el = document.getElementById(id);
        if (el) el.remove();
      });
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <div className="pt-20 md:pt-20">
        <Hero />
        <PainPoint />
        <Services />
        <USPComparison />
        <Process />
        <SelfDiagnosis />
        <Testimonials />
        <FAQ />
        <FinalCTA />
      </div>
      <Footer />
    </div>
  );
}
